import { useEffect, useRef } from "react";

import { useTestRunParams } from "src/flowContainer/TestRunsSidePane/hooks";
import { useJobRun, useJobSources } from "src/jobs/api/queries";
import { getJobRunFlags, JobRun } from "src/jobs/types";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { logger } from "src/utils/logger";

type LoadTestRunEventDetails = {
  jobRun: JobRun;
  datasetId: string;
  datasetEtag: string;
};

type Callback = (arg: LoadTestRunEventDetails) => void;

export const useLoadTestRunListener = (callback: Callback) => {
  const { workspace } = useAuthoringContext();
  const { currentTestRunParam } = useTestRunParams();

  const { data: jobRun } = useJobRun({
    baseURL: workspace.base_url!,
    jobId: currentTestRunParam.jobId,
    runId: currentTestRunParam.runId,
    enabled: !!(currentTestRunParam.jobId && currentTestRunParam.runId),
  });

  const { data: sources } = useJobSources(
    workspace.base_url!,
    currentTestRunParam.jobId,
  );

  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    const source = sources?.find((source) => source.id === jobRun?.source_id);
    if (jobRun && source) {
      const datasetId =
        source.configuration.type === "dataset"
          ? source.configuration.dataset_id
          : "";

      const { isJobTerminated } = getJobRunFlags(jobRun.status.type);

      if (isJobTerminated) {
        logger.warn("Job run is terminated, skipping load", jobRun);
        return;
      }

      callbackRef.current({
        jobRun,
        datasetId: datasetId,
        datasetEtag: jobRun.source_etag,
      });
    }
  }, [jobRun, sources]);
};
